// What a failed settlement action SAYS to an admin.
//
// The server's sentence is written for whoever reads the log: "line item is
// already released" names a row in a table an admin has never seen, and reading
// it back verbatim asks them to translate our internals into their own words.
// Response text is therefore never rendered — the surface renders the copy
// below, keyed on the API's error code (`api-contracts.md` §1.2), and anything
// unmapped falls back to the generic line rather than guessing.
// /* §edge 5 what a failed action says */

/**
 * The API error codes a settlement action can come back with. A closed set:
 * anything outside it is deliberately unmapped and reads as the generic
 * failure.
 */
export const SettlementErrorCode = {
  ReassignConflict: 'REASSIGN_CONFLICT',
  MoveToNextBidderDisabled: 'MOVE_TO_NEXT_BIDDER_DISABLED',
  RefundFailed: 'REFUND_FAILED',
  RefundExceedsCharge: 'REFUND_EXCEEDS_CHARGE',
  InvoiceRevisionStale: 'INVOICE_REVISION_STALE',
  IdempotentReplayMismatch: 'IDEMPOTENT_REPLAY_MISMATCH',
  PaymentMethodDeclined: 'PAYMENT_METHOD_DECLINED',
  RoleInsufficient: 'ROLE_INSUFFICIENT',
  OrgScopeViolation: 'ORG_SCOPE_VIOLATION',
  NotFound: 'NOT_FOUND',
  NotPaid: 'NOT_PAID',
  Conflict: 'CONFLICT',
  ValidationFailed: 'VALIDATION_FAILED',
  RateLimited: 'RATE_LIMITED',
  Internal: 'INTERNAL',
} as const;

export type SettlementErrorCodeValue =
  (typeof SettlementErrorCode)[keyof typeof SettlementErrorCode];

export interface ActionFailureCopy {
  /** The toast headline: what happened, in the admin's vocabulary. */
  summary: string;
  /** The line under it: what is true now, and what to do next. */
  detail: string;
}

/**
 * The unknown-code landing. It commits to the one fact every failed settlement
 * action shares — the write did not happen — because an admin's next move
 * depends on whether they have to undo anything.
 */
export const GENERIC_ACTION_FAILURE: ActionFailureCopy = {
  summary: 'Something went wrong. Nothing was changed.',
  detail: 'Try again. If it keeps happening, refresh the page.',
};

const ADMIN_ERROR_COPY: Record<SettlementErrorCodeValue, ActionFailureCopy> = {
  [SettlementErrorCode.ReassignConflict]: {
    summary: 'This item moved while you were deciding',
    detail: 'Someone else placed or released it. The screen has been re-read. Check where it sits now before trying again.',
  },
  [SettlementErrorCode.MoveToNextBidderDisabled]: {
    summary: 'Reassigning items is turned off for this organization',
    detail: 'Nothing was changed. Ask a Neon One administrator to enable it.',
  },
  [SettlementErrorCode.RefundFailed]: {
    summary: 'The refund was declined, so the item was not released',
    detail: 'The money is still with the bidder and the item still sits on their invoice. Try again, or refund it from the payment processor.',
  },
  [SettlementErrorCode.RefundExceedsCharge]: {
    summary: 'These items add up to more than is left on the payment',
    detail: 'Nothing was refunded or released. Contact Neon One support to settle the rest of this payment.',
  },
  [SettlementErrorCode.InvoiceRevisionStale]: {
    summary: 'This invoice changed since you opened it',
    detail: 'Nothing was charged. Review the new total, then take the payment again.',
  },
  [SettlementErrorCode.IdempotentReplayMismatch]: {
    summary: 'This action was already sent with different details',
    detail: 'Nothing was changed. Close this, re-read the item, and start the action again.',
  },
  [SettlementErrorCode.PaymentMethodDeclined]: {
    summary: 'The card was declined',
    detail: 'Nothing was charged. Ask the bidder for another payment method.',
  },
  [SettlementErrorCode.RoleInsufficient]: {
    summary: 'Your role cannot take this action',
    detail: 'Nothing was changed. Ask an administrator to do it for you.',
  },
  [SettlementErrorCode.OrgScopeViolation]: {
    summary: 'That record belongs to another organization',
    detail: 'Nothing was changed.',
  },
  [SettlementErrorCode.NotPaid]: {
    summary: 'Could not regenerate receipt',
    detail: 'Only paid and partially refunded invoices can be regenerated.',
  },
  [SettlementErrorCode.NotFound]: {
    summary: 'This item is no longer on the invoice',
    detail: 'It was moved or removed elsewhere. Refresh the console to see where it is now.',
  },
  [SettlementErrorCode.Conflict]: {
    summary: 'This item moved while you were deciding',
    detail: 'The screen has been re-read. Check where it sits now before trying again.',
  },
  [SettlementErrorCode.ValidationFailed]: {
    summary: 'Something in that request did not add up',
    detail: 'Nothing was changed. Check the choices you made and try again.',
  },
  [SettlementErrorCode.RateLimited]: {
    summary: 'Too many actions at once',
    detail: 'Nothing was changed. Wait a moment and try again.',
  },
  [SettlementErrorCode.Internal]: GENERIC_ACTION_FAILURE,
};

function isSettlementErrorCode(code: string | null | undefined): code is SettlementErrorCodeValue {
  return code != null && Object.prototype.hasOwnProperty.call(ADMIN_ERROR_COPY, code);
}

/**
 * Admin-facing copy for a failed action.
 *
 * `fallback` lets a surface name the action it was in the middle of ("Could not
 * reassign the lot") where that reads better than the generic line; it is used
 * only when the code is unmapped or absent. The server's own message is never
 * an input here — there is no argument to pass it through.
 */
export function settlementErrorCopy(
  code: string | null | undefined,
  fallback: ActionFailureCopy = GENERIC_ACTION_FAILURE,
): ActionFailureCopy {
  return isSettlementErrorCode(code) ? ADMIN_ERROR_COPY[code] : fallback;
}
